import * as Sentry from "@sentry/react-native";
import { ParseError, parsePhoneNumberWithError } from "libphonenumber-js";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { StyleSheet } from "react-native";
import * as yup from "yup";
import { Layout } from "~/components/Layout";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { Field } from "~/components/V2/Field";
import { Container } from "~/components/V2/PageContainer";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import { useStore } from "~/mobx/utils/useStore";
import { EditAddressModal } from "./EditAddressModal";
import { EditTextInputModal } from "./EditTextInputModal";

const S = StyleSheet.create({
  logoutButton: { maxWidth: 110, width: "100%" },
  container: {
    paddingHorizontal: 0,
  },
});

export const SettingsContactDetailsScreen = observer(
  function SettingsContactDetailsScreen() {
    const store = useStore();
    const errorAlert = useErrorAlertV2();

    const [isEditAddressModalVisible, setIsEditAddressModalVisible] =
      useState(false);

    const [isEditMobilePhoneVisible, setIsEditMobilePhoneVisible] =
      useState(false);

    const [isEditEmailVisible, setIsEditEmailVisible] = useState(false);

    const activeUser = store.authStore.activeUser;

    if (!activeUser) {
      return null;
    }

    return (
      <Screen withBottomInset withTopInset preventScroll>
        <Layout>
          <Container
            styleBottomView={S.container}
            title={intl.formatMessage({
              id: "Y294VV",
              defaultMessage: "Settings",
              description: "Title for the settings page",
            })}
          >
            <EditAddressModal
              isVisible={isEditAddressModalVisible}
              onClose={() => {
                setIsEditAddressModalVisible(false);
              }}
              user={activeUser}
            />

            <EditTextInputModal
              keyboardType="phone-pad"
              isVisible={isEditMobilePhoneVisible}
              onClose={() => {
                setIsEditMobilePhoneVisible(false);
              }}
              title={intl.formatMessage({
                id: "XcOpbS",
                defaultMessage: "Update mobile number",
                description: "Title for updating mobile number",
              })}
              onUpdate={async (value) => {
                try {
                  await store.userStore.updateActiveUser({
                    phoneNumber: value,
                  });
                } catch (error) {
                  console.log("Error while updating mobile phone", error);
                  errorAlert(error);
                  Sentry.captureException(error);
                }
              }}
              placeholder={intl.formatMessage({
                id: "R2Y70i",
                defaultMessage: "Enter your mobile number",
                description: "Placeholder inside text input",
              })}
              initialValue={activeUser.phoneNumber}
              validationSchema={yup
                .string()
                .required()
                .test(
                  "not-only-spaces",
                  intl.formatMessage({
                    id: "KBojtB",
                    description: "Error message for empty phone number",
                    defaultMessage: "Phone number is required",
                  }),
                  (value) => !!value && value.trim().length > 0,
                )
                .test("is-phone-number", (value, ctx) => {
                  if (!!value && value.trim()) {
                    try {
                      parsePhoneNumberWithError(value, {
                        extract: false,
                      });

                      return true;
                    } catch (error) {
                      if (error instanceof ParseError) {
                        if (error.message === "TOO_LONG") {
                          return ctx.createError({
                            message: intl.formatMessage({
                              id: "pB8SjL",
                              description:
                                "Error message for too long phone number",
                              defaultMessage: "The phone number is too long",
                            }),
                          });
                        } else if (error.message === "TOO_SHORT") {
                          return ctx.createError({
                            message: intl.formatMessage({
                              id: "l8lO/0",
                              description:
                                "Error message for too short phone number",
                              defaultMessage: "The phone number is too short",
                            }),
                          });
                        } else {
                          return ctx.createError({
                            message: intl.formatMessage({
                              id: "N/1f86",
                              description:
                                "Error message for invalid phone number",
                              defaultMessage:
                                "The phone number you entered is not valid",
                            }),
                          });
                        }
                      } else {
                        errorAlert(error);
                        Sentry.captureException(error);
                        return ctx.createError({
                          message: intl.formatMessage({
                            id: "N/1f86",
                            description:
                              "Error message for invalid phone number",
                            defaultMessage:
                              "The phone number you entered is not valid",
                          }),
                        });
                      }
                    }
                  }
                  return ctx.createError({
                    message: intl.formatMessage({
                      id: "KBojtB",
                      description: "Error message for empty phone number",
                      defaultMessage: "Phone number is required",
                    }),
                  });
                })}
            />

            <EditTextInputModal
              isVisible={isEditEmailVisible}
              onClose={() => {
                setIsEditEmailVisible(false);
              }}
              title={intl.formatMessage({
                id: "qPJe3H",
                defaultMessage: "Update e-mail address",
                description: "Tile for updating e-mail address",
              })}
              onUpdate={async (value) => {
                try {
                  await store.userStore.updateActiveUser({
                    email: value,
                  });
                } catch (error) {
                  console.log("Error while updating email", error);
                  errorAlert(error);
                  Sentry.captureException(error);
                }
              }}
              placeholder={intl.formatMessage({
                id: "Lj4b+u",
                defaultMessage: "Enter your e-mail address",
                description: "Placeholder inside text input",
              })}
              initialValue={activeUser.email}
              validationSchema={yup.string().email().required()}
            />

            {/* Subtitle */}
            <View paddingHorizontal24 paddingVertical8>
              <BrandText sizeH4 weightExtraBold colorBlack>
                {intl.formatMessage({
                  id: "a8I10Q",
                  defaultMessage: "Contact details",
                  description: "Title for contact details",
                })}
              </BrandText>
            </View>

            {/* Start form */}

            {/* Name */}
            <Field
              label={intl.formatMessage({
                id: "OMbXig",
                defaultMessage: "Name",
                description: "Label for the name input",
              })}
              value={activeUser.fullName}
            />

            {/* Address */}
            <Field
              onPress={() => {
                setIsEditAddressModalVisible(true);
              }}
              label={intl.formatMessage({
                id: "7Vk55F",
                defaultMessage: "Address",
                description: "Label for the address input",
              })}
              value={activeUser.fullAddress}
              isEditable
            />

            {/* Mobile */}
            <Field
              onPress={() => {
                setIsEditMobilePhoneVisible(true);
              }}
              label={intl.formatMessage({
                id: "VzXPY/",
                defaultMessage: "Mobile",
                description: "Label for the mobile input",
              })}
              value={activeUser.phoneNumber}
              isEditable
            />
            {/* E-mail */}
            <Field
              onPress={() => {
                setIsEditEmailVisible(true);
              }}
              label={intl.formatMessage({
                id: "P4eWiS",
                defaultMessage: "E-mail",
                description: "Label for the e-mail input",
              })}
              value={activeUser.email}
              isLast
              isEditable
            />

            {/* End form */}
            <Spacer extraExtraLarge />
            <Spacer large />
          </Container>
        </Layout>
      </Screen>
    );
  },
);
